import { ref, readonly, type Ref } from 'vue';
import Emitter from '@/services/Emitter';
import { type ApiRequest, createApiRequest, mapApiResponse } from '@/models/ApiRequest';

export interface ApiRequestLoadingEvent {
    key: string;
    isLoading: boolean;
}

/**
 * Composable that manages the full lifecycle of an API request.
 *
 * - Exposes reactive `state: ApiRequest<T>` tracking IsLoading, IsSuccess, IsError, Data, etc.
 * - Emits 'apiRequest:loading' events via Emitter so any component (local or global) can
 *   subscribe and decide independently whether to show a spinner or indicator.
 * - Does NOT trigger the legacy global spinner automatically — callers opt in via listeners.
 *
 * @param key  Optional identifier for this request (used in loading events). Defaults to 'default'.
 *
 * @example
 * const { state, execute } = useApiRequest<ClientModel[]>('clients');
 *
 * await execute(() => Api.loadClients());
 *
 * // In template:
 * // v-if="state.IsLoading" → show spinner
 * // v-if="state.IsError"   → show error message (state.Message / state.Errors)
 * // state.Data             → the typed result
 *
 * @example Listening globally to all loading events:
 * Emitter.on('apiRequest:loading', (event: ApiRequestLoadingEvent) => {
 *   if (event.isLoading) showGlobalSpinner();
 *   else hideGlobalSpinner();
 * });
 */
export function useApiRequest<T = any>(key: string = 'default') {
    const state = ref(createApiRequest<T>()) as Ref<ApiRequest<T>>;

    const emitLoading = (isLoading: boolean) => {
        Emitter.emit('apiRequest:loading', { key, isLoading } as ApiRequestLoadingEvent);
    };

    /**
     * Executes an API call and populates `state` with the lifecycle result.
     *
     * Pass any ApiService method call as the `fn` argument. The function
     * should return the raw full response from Http.getFullResponse / Http.postFullResponse,
     * or any value — the composable will map it correctly.
     *
     * @param fn  Async function that returns an ApiResponse-shaped object or typed data.
     */
    const execute = async (fn: () => Promise<any>): Promise<ApiRequest<T>> => {
        state.value = {
            ...createApiRequest<T>(),
            IsLoading: true,
        };
        emitLoading(true);

        try {
            const result = await fn();

            // If the result looks like a full ApiResponse (has Status or Success field), map it.
            // Otherwise treat the result as raw Data (legacy Http.get behaviour).
            const isFullResponse =
                result !== null &&
                typeof result === 'object' &&
                ('Status' in result || 'Success' in result);

            if (isFullResponse) {
                state.value = mapApiResponse<T>(result) as ApiRequest<T>;
            } else {
                state.value = {
                    IsLoading: false,
                    IsSuccess: true,
                    IsError: false,
                    Data: result as T,
                    Message: null,
                    Errors: null,
                    Redirect: null,
                    Meta: null,
                };
            }
        } catch (err: any) {
            const message: string =
                err?.Message || err?.message || 'An unexpected error occurred.';
            const errors: string[] | null = err?.Errors || err?.errors || null;

            state.value = {
                IsLoading: false,
                IsSuccess: false,
                IsError: true,
                Data: null,
                Message: message,
                Errors: errors,
                Redirect: null,
                Meta: null,
            };
        } finally {
            emitLoading(false);
        }

        return state.value;
    };

    /**
     * Resets state back to the initial idle state.
     */
    const reset = () => {
        state.value = createApiRequest<T>();
    };

    return {
        state: readonly(state),
        execute,
        reset,
    };
}

export default useApiRequest;
